import React from 'react'
import { ThemeProvider } from '../context/ThemeContext'
import { useTheme } from '../context/ThemeContext'

function PrivacyPolicyContent() {
  const { isDark, toggleTheme } = useTheme()

  const sectionStyle = {
    marginBottom: '2.5rem',
  }

  const headingStyle = {
    fontFamily: 'Georgia, serif',
    fontSize: '1.05rem',
    fontWeight: '700',
    color: 'var(--text)',
    marginBottom: '0.75rem',
    marginTop: 0,
    paddingBottom: '0.5rem',
    borderBottom: '1px solid var(--border)',
  }

  const bodyStyle = {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: '0.88rem',
    color: 'var(--text-muted)',
    lineHeight: '1.8',
    margin: 0,
  }

  const linkStyle = {
    color: '#c9a84c',
    textDecoration: 'none',
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 1.5rem',
        height: '58px',
        background: 'var(--bg)',
        borderBottom: '1px solid var(--border)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <a
          href="/"
          style={{ fontFamily: 'Georgia, serif', fontSize: '1.15rem', fontWeight: '700', color: 'var(--text)', textDecoration: 'none' }}
        >
          DebtCrush
        </a>
        <button
          onClick={toggleTheme}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', padding: '0.44rem 0.6rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.85rem', cursor: 'pointer', lineHeight: 1 }}
        >
          {isDark ? '☀' : '🌙'}
        </button>
      </header>

      {/* Content */}
      <main style={{ maxWidth: '760px', margin: '0 auto', padding: '3rem 1.5rem 5rem' }}>

        {/* Page title */}
        <div style={{ marginBottom: '3rem' }}>
          <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
            Legal
          </div>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '2rem', fontWeight: '700', color: 'var(--text)', margin: '0 0 0.5rem' }}>
            Privacy Policy
          </h1>
          <p style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', margin: 0 }}>
            Last updated: June 2026 &nbsp;·&nbsp; DebtCrush
          </p>
        </div>

        {/* Intro */}
        <div style={{ ...sectionStyle, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.25rem 1.5rem', borderLeft: '3px solid #c9a84c' }}>
          <p style={{ ...bodyStyle, color: 'var(--text)' }}>
            At <strong>DebtCrush</strong>, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our debt management platform at{' '}
            <a href="https://debtcrush.in" style={linkStyle}>debtcrush.in</a>.
            By using DebtCrush, you agree to the practices described in this policy.
          </p>
        </div>

        {/* 1. Information We Collect */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. Information We Collect</h2>
          <p style={bodyStyle}>
            We collect information you provide directly when you create an account or use our services:
          </p>
          <ul style={{ ...bodyStyle, paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Account information</strong> — your name, email address, and profile details.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Financial data</strong> — loan details, EMI amounts, insurance policies, expenses, investments, and emergency fund information that you enter.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Usage data</strong> — how you interact with the app, pages visited, features used, and session duration.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Device information</strong> — browser type, operating system, and IP address for security and analytics purposes.</li>
          </ul>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            We do <strong>not</strong> collect your bank credentials, payment card numbers, or connect to any banking systems.
          </p>
        </div>

        {/* 2. How We Use Your Information */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. How We Use Your Information</h2>
          <p style={bodyStyle}>We use the information we collect to:</p>
          <ul style={{ ...bodyStyle, paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}>Provide, operate, and improve the DebtCrush platform and its features.</li>
            <li style={{ marginBottom: '0.5rem' }}>Generate your financial summaries, EMI reminders, health scores, and personalized suggestions.</li>
            <li style={{ marginBottom: '0.5rem' }}>Send you important service notifications and updates about your account.</li>
            <li style={{ marginBottom: '0.5rem' }}>Ensure the security and integrity of your account.</li>
            <li style={{ marginBottom: '0.5rem' }}>Analyse aggregated, anonymised usage patterns to improve the product.</li>
            <li style={{ marginBottom: '0.5rem' }}>Comply with applicable legal obligations.</li>
          </ul>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            We do not use your financial data for advertising or sell it to any third party for marketing purposes.
          </p>
        </div>

        {/* 3. Data Storage */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. Data Storage</h2>
          <p style={bodyStyle}>
            Your data is stored securely using Supabase, a third-party backend-as-a-service platform. Data is stored on servers with industry-standard encryption at rest (AES-256) and in transit (TLS/HTTPS). We retain your data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where retention is required by law.
          </p>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            Certain preferences (such as theme and sidebar state) are stored locally in your browser using <code style={{ fontFamily: 'monospace', fontSize: '0.82em', color: 'var(--text)' }}>localStorage</code> and are never transmitted to our servers.
          </p>
        </div>

        {/* 4. Data Sharing */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Data Sharing</h2>
          <p style={bodyStyle}>
            We do not sell, trade, or rent your personal information to third parties. We may share limited data only in the following circumstances:
          </p>
          <ul style={{ ...bodyStyle, paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Service providers</strong> — trusted third parties (such as Supabase for database hosting) that assist in operating our platform, under strict confidentiality agreements.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Legal requirements</strong> — when required to comply with applicable law, a court order, or a governmental request.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Business transfers</strong> — in the event of a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction, with prior notice given to you.</li>
          </ul>
        </div>

        {/* 5. Your Rights */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Your Rights</h2>
          <p style={bodyStyle}>You have the following rights regarding your personal data:</p>
          <ul style={{ ...bodyStyle, paddingLeft: '1.5rem', marginTop: '0.75rem' }}>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Access</strong> — request a copy of the personal data we hold about you.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Correction</strong> — request correction of inaccurate or incomplete data via your Profile settings.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Deletion</strong> — request deletion of your account and associated personal data.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Portability</strong> — export your loan and payment data at any time using the built-in PDF and Excel export features.</li>
            <li style={{ marginBottom: '0.5rem' }}><strong style={{ color: 'var(--text)' }}>Objection</strong> — object to certain processing of your data, including any use for analytics.</li>
          </ul>
          <p style={{ ...bodyStyle, marginTop: '0.75rem' }}>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:support@debtcrush.in" style={linkStyle}>support@debtcrush.in</a>.
          </p>
        </div>

        {/* 6. Children's Privacy */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Children's Privacy</h2>
          <p style={bodyStyle}>
            DebtCrush is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child under 18 has provided us with personal data, we will take steps to delete that information promptly. If you believe we have inadvertently collected data from a minor, please contact us immediately at{' '}
            <a href="mailto:support@debtcrush.in" style={linkStyle}>support@debtcrush.in</a>.
          </p>
        </div>

        {/* 7. Changes to Policy */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Changes to This Policy</h2>
          <p style={bodyStyle}>
            We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. When we make significant changes, we will notify you via an in-app notification or by email. The updated policy will always be available at{' '}
            <a href="https://debtcrush.in/privacy-policy" style={linkStyle}>debtcrush.in/privacy-policy</a>{' '}
            with a revised "Last updated" date. Your continued use of DebtCrush after changes are posted constitutes your acceptance of the revised policy.
          </p>
        </div>

        {/* 8. Contact Us */}
        <div style={{ ...sectionStyle, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.5rem' }}>
          <h2 style={{ ...headingStyle, borderBottom: 'none', paddingBottom: 0 }}>8. Contact Us</h2>
          <p style={{ ...bodyStyle, marginTop: '0.5rem' }}>
            If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please reach out to us:
          </p>
          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', minWidth: '60px' }}>Email</span>
              <a href="mailto:support@debtcrush.in" style={{ ...linkStyle, fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: '600' }}>support@debtcrush.in</a>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', minWidth: '60px' }}>Website</span>
              <a href="https://debtcrush.in" style={{ ...linkStyle, fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: '600' }}>debtcrush.in</a>
            </div>
          </div>
        </div>

        {/* Back link */}
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <a
            href="/"
            style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text-muted)', textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '0.08em' }}
            onMouseEnter={e => e.currentTarget.style.color = '#c9a84c'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
          >
            ← Back to DebtCrush
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid var(--border)', padding: '1.5rem', textAlign: 'center' }}>
        <p style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', margin: 0 }}>
          © {new Date().getFullYear()} DebtCrush · All rights reserved
        </p>
      </footer>
    </div>
  )
}

export default function PrivacyPolicy() {
  return (
    <ThemeProvider>
      <PrivacyPolicyContent />
    </ThemeProvider>
  )
}
